import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState, useCallback } from 'react';
import type { Location } from 'types/Location';

export interface CoordinateEditorProps {
    value?: Location;
    disabled?: boolean;
    isGeographic: boolean;
    onChange?: (value: Location | undefined) => void;
    coordinateLabel?: string;
    wkidLabel?: string;
}

export function CoordinateEditor({ value, disabled, isGeographic, onChange, coordinateLabel, wkidLabel }: CoordinateEditorProps) {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draftLat, setDraftLat] = useState<string>('');
    const [draftLon, setDraftLon] = useState<string>('');

    useEffect(() => {
        if (!isEditing) {
            setDraftLon(value?.coordinates?.[0] !== undefined ? String(value.coordinates[0]) : '');
            setDraftLat(value?.coordinates?.[1] !== undefined ? String(value.coordinates[1]) : '');
        }
    }, [isEditing, value?.coordinates?.[0], value?.coordinates?.[1]]);

    const [rangeError, setRangeError] = useState<string | null>(null); // Single generic validation message (empty, NaN, or out-of-range)

    const validateRange = (lat: number, lon: number) => {
        if (isGeographic) {
            const outLat = lat < -90 || lat > 90;
            const outLon = lon < -180 || lon > 180;
            if (outLat || outLon) {
                setRangeError('Coordinates must be within valid geographic ranges: latitude -90..90, longitude -180..180.');
                return false;
            }
        }
        setRangeError(null);
        return true;
    };

    const commitDraft = useCallback(() => {
        const latStr = draftLat.trim();
        const lonStr = draftLon.trim();
        if (latStr === '' || lonStr === '') {
            setRangeError('Coordinates must be within valid geographic ranges: latitude -90..90, longitude -180..180.');
            return;
        }
        const lat = Number(latStr);
        const lon = Number(lonStr);
        if (Number.isNaN(lat) || Number.isNaN(lon)) {
            setRangeError('Coordinates must be within valid geographic ranges: latitude -90..90, longitude -180..180.');
            return;
        }
        const rangeValid = validateRange(lat, lon);
        if (!rangeValid) return;
        onChange?.({ type: 'Point', coordinates: [lon, lat] } as Location);
        setIsEditing(false);
    }, [draftLat, draftLon, isGeographic, onChange]);
 
    const showErrors = !!rangeError;

    if (isEditing) {
        return (
            <Box display="flex" flexDirection="column" gap={0.5} sx={{ width: '100%', flex: 1 }}>
                <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1} width="100%">
                    <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1 }}>
                        {coordinateLabel && <Typography variant="subtitle2" color="text.secondary">{coordinateLabel}</Typography>}
                        {wkidLabel && <Typography variant="subtitle2" color="text.secondary">{wkidLabel}</Typography>}
                    </Box>
                    <Box display="flex" gap={1} sx={{ ml: 'auto', alignItems: 'center' }}>
                        <TextField
                            type="number"
                            size="small"
                            value={draftLat}
                            onChange={(e) => { setDraftLat(e.target.value); setRangeError(null); }}
                            inputProps={{ step: 0.001 }}
                            sx={{ width: 120, '& input': { textAlign: 'end' } }}
                            error={!!rangeError}
                            autoFocus
                        />
                        <TextField
                            type="number"
                            size="small"
                            value={draftLon}
                            onChange={(e) => { setDraftLon(e.target.value); setRangeError(null); }}
                            inputProps={{ step: 0.001 }}
                            sx={{ width: 120, '& input': { textAlign: 'end' } }}
                            error={!!rangeError}
                        />
                        <Tooltip title="Done">
                            <IconButton size="small" aria-label="Done" onClick={commitDraft}>
                                <DoneIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Clear">
                            <IconButton size="small" aria-label="Clear" onClick={clearAll}>
                                <ClearIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                {showErrors && (
                    <Box display="flex" flexDirection="column" gap={0.25} pl={coordinateLabel || wkidLabel ? 0 : 0}>
                        <Typography variant="caption" color="error">{rangeError}</Typography>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={2} sx={{ width: '100%' }} data-testid="coordinate-editor-static">
            <Box display="flex" alignItems="center" gap={1} sx={{ flexGrow: 1 }}>
                {coordinateLabel && <Typography variant="subtitle2" color="text.secondary">{coordinateLabel}</Typography>}
                {wkidLabel && <Typography variant="subtitle2" color="text.secondary">{wkidLabel}</Typography>}
            </Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'right', minWidth: 140 }}>{formatDisplay()}</Typography>
            {!disabled && (
                <Tooltip title="Edit coordinates">
                    <IconButton
                        size="small"
                        aria-label="Edit coordinates"
                        onClick={() => {
                            const lon = value?.coordinates?.[0] !== undefined ? String(value.coordinates[0]) : '';
                            const lat = value?.coordinates?.[1] !== undefined ? String(value.coordinates[1]) : '';
                            setDraftLon(lon);
                            setDraftLat(lat);
                            setIsEditing(true);
                        }}
                    >
                        <EditIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );

    function clearAll(): void {
        setDraftLat('');
        setDraftLon('');
        setRangeError(null);
        onChange?.(undefined);
        setIsEditing(false);
    };

    function formatDisplay(): string {
        if (!value?.coordinates || value.coordinates.length < 2) return '';
        const lat = value.coordinates[1].toFixed(5);
        const lon = value.coordinates[0].toFixed(5);
        return isGeographic ? `${lat}, ${lon}` : `${lon}, ${lat} m`;
    }
}

